// src/ai/socraticTutorDrawer.ts — Sliding Socratic Dialogue Drawer UI (T-5.2.3, FR-AI-004)
// Delivers Clawey's Socratic dialogue in a focused, non-disruptive slide-in drawer.

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';
import type { HintRequest, HintResponse } from './tutorTypes';
import { tutorClient } from './tutorClient';
import { ExplorerPointsManager } from '@storage/explorerPoints';

export interface SocraticDrawerOptions {
  onDismiss?: () => void;
  onClueFound?: () => void;
}

export class SocraticTutorDrawer {
  private container: HTMLElement;
  private isOpen: boolean = false;
  private isLoading: boolean = false;
  private currentRequest: HintRequest | null = null;
  private currentResponse: HintResponse | null = null;
  private onDismiss?: () => void;
  private onClueFound?: () => void;

  constructor(options: SocraticDrawerOptions = {}) {
    this.container = document.createElement('div');
    this.container.id = 'socratic-tutor-drawer-root';
    this.container.className = 'socratic-drawer-wrapper';
    this.onDismiss = options.onDismiss;
    this.onClueFound = options.onClueFound;
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getIsOpen(): boolean {
    return this.isOpen;
  }

  getCurrentResponse(): HintResponse | null {
    return this.currentResponse;
  }

  /**
   * Open drawer and trigger Socratic hint request
   */
  async openWithRequest(request: HintRequest): Promise<void> {
    this.currentRequest = request;
    this.isOpen = true;
    this.isLoading = true;
    this.currentResponse = null;
    this.render();

    soundEngine.playTap();

    try {
      const response = await tutorClient.requestHint(request);
      this.currentResponse = response;
      this.isLoading = false;
      this.render();
      soundEngine.playSuccess();
    } catch {
      this.isLoading = false;
      this.render();
    }
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.render();
    if (this.onDismiss) {
      this.onDismiss();
    }
  }

  render(): HTMLElement {
    if (!this.isOpen) {
      this.container.innerHTML = '';
      return this.container;
    }

    this.container.innerHTML = `
      <!-- Backdrop Overlay -->
      <div
        id="socratic-drawer-backdrop"
        style="
          position: fixed;
          inset: 0;
          background: rgba(7, 20, 36, 0.45);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 998;
          animation: fadeInBackdrop 0.2s ease;
        "
      ></div>

      <!-- Slide-in Drawer Container -->
      <aside
        id="socratic-drawer-panel"
        style="
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          max-width: 680px;
          margin: 0 auto;
          background: #FFFDF9;
          border-top: 3px solid ${tokens.color.gildedBrass};
          border-left: 2px solid ${tokens.color.gildedBrass};
          border-right: 2px solid ${tokens.color.gildedBrass};
          border-top-left-radius: 24px;
          border-top-right-radius: 24px;
          box-shadow: 0 -8px 36px rgba(7, 20, 36, 0.25);
          padding: 24px 28px 32px;
          z-index: 999;
          animation: slideUpDrawer 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        "
      >
        <style>
          @keyframes fadeInBackdrop { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUpDrawer { from { transform: translateY(100%); } to { transform: translateY(0); } }
        </style>

        <!-- Drawer Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-size: 2.2rem;">🦞</span>
            <div>
              <div style="font-family: ${tokens.font.heading}; font-size: 1.25rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; line-height: 1.2;">
                Captain Clawey’s Study
              </div>
              <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700; text-transform: uppercase;">
                Socratic Naturalist Guide
              </div>
            </div>
          </div>

          <button
            id="btn-close-drawer"
            type="button"
            class="touch-target"
            style="
              width: 48px;
              height: 48px;
              border: none;
              background: rgba(26, 54, 93, 0.08);
              border-radius: 50%;
              cursor: pointer;
              font-size: 1.25rem;
              display: flex;
              align-items: center;
              justify-content: center;
              color: ${tokens.color.morrisIndigo};
            "
            title="Close Drawer"
          >
            ✕
          </button>
        </div>

        <!-- Content Area -->
        ${this.isLoading ? `
          <div style="text-align: center; padding: 28px 16px;">
            <div style="font-size: 2.5rem; animation: pulseBinoculars 1.2s infinite alternate; margin-bottom: 12px;">🔍</div>
            <div style="font-size: 1.1rem; font-weight: 700; color: ${tokens.color.morrisIndigo};">
              Clawey is polishing his naturalist binoculars...
            </div>
            <div style="font-size: 0.85rem; color: #5A6D86; margin-top: 4px;">
              Examining the evidence and formulating Socratic cues...
            </div>
          </div>
          <style>
            @keyframes pulseBinoculars { 0% { transform: scale(0.9); } 100% { transform: scale(1.1); } }
          </style>
        ` : this.currentResponse ? `
          <div style="margin-bottom: 20px;">
            <!-- Socratic Hint Balloon -->
            <div style="
              padding: 18px 22px;
              background: rgba(255, 246, 229, 0.95);
              border-left: 4px solid ${tokens.color.gildedBrass};
              border-radius: 14px;
              margin-bottom: 16px;
              font-size: 1.15rem;
              line-height: 1.6;
              color: #2D3E50;
              box-shadow: 0 2px 12px rgba(229, 169, 60, 0.15);
            ">
              ${this.currentResponse.hint}
            </div>

            <!-- Guiding Follow-up Prompt (FR-AI-004) -->
            <div style="
              padding: 14px 20px;
              background: rgba(0, 229, 255, 0.12);
              border-left: 4px solid ${tokens.color.biolumCyan};
              border-radius: 12px;
              font-size: 1.05rem;
              font-weight: 800;
              color: ${tokens.color.morrisIndigo};
              line-height: 1.45;
            ">
              🧭 <strong>Prompt:</strong> ${this.currentResponse.interactivePrompt}
            </div>
          </div>

          <!-- Action Buttons -->
          <div style="display: flex; gap: 12px; justify-content: flex-end; flex-wrap: wrap;">
            <button
              id="btn-drawer-clue-found"
              type="button"
              class="btn-primary touch-target"
              style="padding: 12px 24px; font-size: 1rem; font-weight: 800;"
            >
              💡 I Spot the Clue Now! (+3 EP) &rarr;
            </button>
          </div>
        ` : `
          <div style="padding: 18px; text-align: center; color: #5A6D86;">
            No guidance available right now. Tap a question to ask Clawey.
          </div>
        `}
      </aside>
    `;

    this.attachEventListeners();
    return this.container;
  }

  private attachEventListeners(): void {
    const backdrop = this.container.querySelector<HTMLElement>('#socratic-drawer-backdrop');
    backdrop?.addEventListener('click', () => {
      this.close();
    });

    const closeBtn = this.container.querySelector<HTMLButtonElement>('#btn-close-drawer');
    closeBtn?.addEventListener('click', () => {
      this.close();
    });

    const clueFoundBtn = this.container.querySelector<HTMLButtonElement>('#btn-drawer-clue-found');
    clueFoundBtn?.addEventListener('click', () => {
      soundEngine.playSuccess();
      ExplorerPointsManager.award('hint_then_correct', this.currentRequest?.questionId);
      this.close();
      if (this.onClueFound) {
        this.onClueFound();
      }
    });
  }
}
